import React, { useState, useRef, FormEvent, useEffect, useCallback } from "react";

import { DeviceScrollEnum } from "../../types/suggestions";
import { IHandleKeyboardNavigation } from "../../types/keyboard-navigation";
import { ISuggestionResponse } from "../../types/suggestion-response";
import { SearchBoxProps } from "../../types/searchbox";

import { L10nContext, NavigationContext } from "../common/contexts";
import { PageViewLogDataId } from "../../util/pageViewLogDataId";
import { useKeyboardNavigation } from "../common/keyboard-navigation";
import { useL10n } from "../common/l10n";
import debounce from "../../util/debounce";
import defaultSuggestionResponse from "../../util/defaultSuggestionResponse";
import SearchIcon from "../../Assets/svg/Search_2021_icon";
import SuggestionColumns from "../suggestion-list/suggestion-columns";
import SuggestionList from "../suggestion-list/suggestion-list";
import useSuggestionsRenderView from "../suggestion-list/suggestions-render-view";

import "./searchbox-megamenu.scss";
import "../suggestion-list/suggestions-megamenu.scss";

const SearchBoxMegaMenu = ({ detectedContext, ...props }: SearchBoxProps) => {
    const { isWebShop, isMobileShop, device, customerType } = detectedContext;
    const SEARCH_TERM_LENGTH_LIMIT = 50;
    const TOP_SEARCH_QUERIES_LIMIT = isWebShop ? 10 : 5;
    const inputTabNavAttribute = { focusable: true };
    const showColumns = useSuggestionsRenderView("megamenu", isMobileShop);

    const [focus, setFocus] = useState(false);
    const [input, setInput] = useState("");
    const [validSearchTerm, setValidSearchTerm] = useState("");
    const [suggestions, setSuggestions] = useState<ISuggestionResponse>(
        defaultSuggestionResponse()
    );
    const [hasSuggestions, setHasSuggestions] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const [initialScreenHeight, setInitialScreenHeight] = useState(0);
    const [lastScreenOrientation, setLastScreenOrientation] = useState<OrientationType | null>(
        null
    );
    const [mobileDeviceKeyboardWasOpened, setMobileDeviceKeyboardWasOpened] = useState(false);

    const scopeSswRef = useRef<HTMLDivElement | null>(null);
    const searchBoxRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const focusRef = useRef(focus);
    const abortControllerRef = useRef<AbortController | null>(null);
    const keyboardNavColumnElementRef = useRef<IHandleKeyboardNavigation | null>(null);
    const keyboardNavListElementRef = useRef<IHandleKeyboardNavigation | null>(null);

    const pageViewLogDataId = PageViewLogDataId.get();

    const l10n = useL10n(props.l10n);

    useEffect(() => {
        setInitialScreenHeight(window.screen.availHeight);
        setLastScreenOrientation(window.screen.orientation.type);

        if (searchBoxRef.current) {
            scopeSswRef.current = searchBoxRef.current.closest(
                ".scope-ssw"
            ) as HTMLDivElement | null;
        }
    }, []);

    const handleClick = useCallback((event: Event) => {
        const target = event.target as HTMLElement;

        if (!searchBoxRef.current?.contains(target)) handleBlur();
    }, []);

    useEffect(() => {
        focusRef.current = focus;
    }, [focus]);

    useEffect(() => {
        const hasSuggestions =
            suggestions.articles.length > 0 ||
            suggestions.flexibleSets.length > 0 ||
            suggestions.contents.length > 0 ||
            suggestions.topSearchQueries.length > 0;
        setHasSuggestions(hasSuggestions);
    }, [suggestions]);

    useEffect(() => {
        const showSuggestions = hasSuggestions && focus && !!validSearchTerm;
        setShowSuggestions(showSuggestions);
    }, [hasSuggestions, focus, validSearchTerm]);

    useEffect(() => {
        document.addEventListener("mousedown", handleClick);

        return () => {
            document.removeEventListener("mousedown", handleClick);
        };
    }, [handleClick]);

    const handleMobileResize = useCallback(() => {
        // searchbox should loose focus when mobile device keyboard is closed
        const assumedMinimumKeyboardHeight = 200;

        if (lastScreenOrientation != window.screen.orientation.type) {
            // screen orientation changed, values must be adjusted
            setLastScreenOrientation(window.screen.orientation.type);
            setInitialScreenHeight(window.screen.availHeight);
        } else if (
            mobileDeviceKeyboardWasOpened &&
            initialScreenHeight <= window.screen.availHeight
        ) {
            // mobile device keyboard was most likely closed
            handleBlur();
            setMobileDeviceKeyboardWasOpened(false);
        } else if (
            !mobileDeviceKeyboardWasOpened &&
            initialScreenHeight > window.screen.availHeight
        ) {
            // mobile device keyboard was most likely opened
            setMobileDeviceKeyboardWasOpened(
                initialScreenHeight - window.screen.availHeight >= assumedMinimumKeyboardHeight
            );
        }
    }, [initialScreenHeight, lastScreenOrientation, mobileDeviceKeyboardWasOpened]);

    useEffect(() => {
        if (!isMobileShop) return;

        window.addEventListener("resize", handleMobileResize, false);

        return () => {
            window.removeEventListener("resize", handleMobileResize, false);
        };
    }, [handleMobileResize, isMobileShop]);

    const navigationApi = useKeyboardNavigation(
        showColumns
            ? () => keyboardNavColumnElementRef.current?.getNavigationElements() ?? []
            : () => keyboardNavListElementRef.current?.getNavigationElements() ?? []
    );

    useEffect(() => {
        const suggestionsVisible = hasSuggestions && showSuggestions;
        if (!suggestionsVisible) {
            navigationApi.resetNavigation();
        }
    }, [hasSuggestions, showSuggestions, navigationApi]);

    const handleBlur = () => {
        if (!focusRef.current) return;

        setFocus(false);
        inputRef.current?.blur();
    };

    const handleFocus = () => {
        if (focusRef.current) return;
        setFocus(true);
    };

    const activateFocusHelper = () => {
        handleFocus();
        inputRef.current?.focus();
    };

    const resetSuggestionResponse = () => {
        setSuggestions(defaultSuggestionResponse());
    };

    const fetchSuggestions = async (input: string) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const headers: Record<string, string> = pageViewLogDataId
            ? { "X-PageView-ID": pageViewLogDataId }
            : {};

        try {
            const res = await fetch(
                `${props.apiUrl}suggestion/?searchTerm=${encodeURIComponent(
                    input
                )}&topSearchQueriesLimit=${TOP_SEARCH_QUERIES_LIMIT}`,
                { headers, signal: controller.signal }
            );

            if (!res.ok) throw new Error("Network response was not ok");

            const response = await res.json();

            if (
                response.articles.length ||
                response.flexibleSets.length ||
                response.contents.length ||
                response.topSearchQueries.length
            ) {
                setSuggestions(response);
            } else {
                resetSuggestionResponse();
            }
        } catch (error) {
            if (error instanceof Error && error.name !== "AbortError") {
                resetSuggestionResponse();
            }
        } finally {
            abortControllerRef.current = null;
        }
    };

    const debouncedFetch = useRef<((input: string) => void) & { cancel: () => void }>(
        debounce((input: string) => {
            setValidSearchTerm(input);
            fetchSuggestions(input);
        }, 250)
    );

    const sanatizeInput = (input: string) => {
        const validInput = input.length > SEARCH_TERM_LENGTH_LIMIT ? input.slice(0, 50) : input;
        return validInput;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value ?? "";
        const sanatizedInput = sanatizeInput(value);
        setInput(sanatizedInput);

        const trimmedInput = sanatizedInput.trim();
        if (!trimmedInput) {
            debouncedFetch.current.cancel();
            setValidSearchTerm("");
            resetSuggestionResponse();
        } else {
            debouncedFetch.current(trimmedInput);
        }
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const trimmedInput = input.trim();
        if (!trimmedInput) {
            handleBlur();
            return;
        }

        window.location.href = `${props.searchTarget}?query=${encodeURIComponent(trimmedInput)}`;
    };

    return (
        <L10nContext.Provider value={l10n}>
            <div
                className={
                    "searchbox-megamenu" +
                    ` ${focus ? "focus" : ""}` +
                    ` ${showSuggestions ? "show-suggestions" : ""}` +
                    ` ${props.tenant}` +
                    ` ${props.isEsApp ? "esApp" : ""}`
                }
                ref={searchBoxRef}
            >
                <form
                    className={"search-megamenu"}
                    onSubmit={handleSubmit}
                    onClick={activateFocusHelper}
                >
                    <button
                        type="submit"
                        className="search-megamenu-button"
                        aria-label={l10n.defaultTerm}
                    >
                        <SearchIcon />
                    </button>

                    <span className="transition-wrapper">
                        <span
                            className="input-sizer"
                            data-value={validSearchTerm || (focus ? "" : l10n.defaultTerm)}
                        >
                            <input
                                ref={inputRef}
                                value={input}
                                maxLength={50}
                                onChange={handleChange}
                                onFocus={handleFocus}
                                placeholder={focus ? "" : l10n.defaultTerm}
                                className="search-megamenu-input"
                                type="text"
                                name="query"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="none"
                                spellCheck="false"
                                aria-label={l10n.defaultTerm}
                                size={1}
                                {...inputTabNavAttribute}
                            />
                        </span>
                    </span>
                </form>

                <hr className={"grid-line"} />

                <div id={`new-suggestions-${props.view}`} className={"new-suggestions"}>
                    {showSuggestions && (
                        <>
                            {showColumns ? (
                                <NavigationContext.Provider value={navigationApi}>
                                    <SuggestionColumns
                                        navigationRef={keyboardNavColumnElementRef}
                                        portal={props.portal}
                                        culture={props.culture}
                                        searchTarget={props.searchTarget}
                                        trackingEndpoint={props.trackingEndpoint}
                                        articles={suggestions.articles}
                                        flexibleSets={suggestions.flexibleSets}
                                        contents={suggestions.contents}
                                        topSearchQueries={suggestions.topSearchQueries}
                                        priceMandantSettings={
                                            suggestions.priceMandantSettingsArticles
                                        }
                                        priceMandantSettingsV2={
                                            suggestions.priceMandantSettingsFlexibleSet
                                        }
                                        customerType={customerType}
                                        searchTerm={validSearchTerm}
                                        isMobileShop={isMobileShop}
                                        view={props.view}
                                        pageViewLogDataId={pageViewLogDataId}
                                        deviceScrollClass={DeviceScrollEnum.None}
                                        deviceType={device}
                                    />
                                </NavigationContext.Provider>
                            ) : (
                                <NavigationContext.Provider value={navigationApi}>
                                    <SuggestionList
                                        navigationRef={keyboardNavListElementRef}
                                        portal={props.portal}
                                        culture={props.culture}
                                        searchTarget={props.searchTarget}
                                        trackingEndpoint={props.trackingEndpoint}
                                        articles={suggestions.articles}
                                        flexibleSets={suggestions.flexibleSets}
                                        contents={suggestions.contents}
                                        topSearchQueries={suggestions.topSearchQueries}
                                        priceMandantSettings={
                                            suggestions.priceMandantSettingsArticles
                                        }
                                        priceMandantSettingsV2={
                                            suggestions.priceMandantSettingsFlexibleSet
                                        }
                                        customerType={customerType}
                                        searchTerm={validSearchTerm}
                                        isMobileShop={isMobileShop}
                                        view={props.view}
                                        pageViewLogDataId={pageViewLogDataId}
                                        deviceType={device}
                                    />
                                </NavigationContext.Provider>
                            )}
                        </>
                    )}
                </div>
            </div>
        </L10nContext.Provider>
    );
};

export default SearchBoxMegaMenu;
